/**
 * הזמנת משתמש — and the field that is deliberately absent. OA-0001 §3.7.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THERE IS NO ROLE ON THIS FORM, AND ITS ABSENCE IS A CONTROL.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * While an invitation carried a role, a principal holding only `tenant.user.invite` could mint an
 * account holding ANY role in the tenant — including a copy of the bootstrap administrator — and
 * then use it. That collapses "what a user may DO" into "who may invite", which OA-0001 §3.7
 * forbids in as many words: *"They do not collapse into one admin flag."*
 *
 * So an invitee arrives with NO capabilities. Granting them is a separate act: a separate screen, a
 * separate capability (`tenant.role.manage`), a separate route and a separate table. The route this
 * form posts to — `POST /api/tenant/users/invitations` — accepts `email` and `displayName` and
 * NOTHING ELSE, and a role field here would be a field the server refuses to read.
 *
 * The screen says all of that in Hebrew, because an administrator who cannot find the role dropdown
 * will otherwise assume the product is unfinished rather than deliberate.
 *
 * ── WHAT ELSE IS ABSENT ─────────────────────────────────────────────────────────────────────────
 *
 *   · No password field, and no password anywhere in the response. 04 §2.2: *"No path may ever
 *     produce, transmit or display a password."* Gate B found Legacy's create-user hard-coding a
 *     default AND RETURNING IT IN THE JSON; the invitation is the shape that replaces it.
 *   · No company. A grant is `companies.assignment.manage`, on its own screen. D5: no role,
 *     ownership flag or seniority implies a grant.
 *   · No seat toggle. Seat capacity is checked by the server BEFORE the invitation is issued, and an
 *     unaccepted invitation consumes no seat until it is redeemed.
 *
 * ── THE GAP THAT WAS NAMED HERE IS NOW CLOSED ───────────────────────────────────────────────────
 *
 * A previous round recorded that the seat-limit refusal — `409 {error: 'limit_reached', limit,
 * current, upgradePath}` — could not reach this screen, because the client's taxonomy kept only a
 * support reference from a conflict body and widening a binding contract is not something one screen
 * may do on its own. `ConflictError` now carries the count, the limit and the upgrade path across, so
 * the refusal here is the same count/limit/route notice 08 §7 requires and that
 * `TenantCompaniesScreen` already showed — one treatment, on both screens, from one component.
 *
 * ── AND A 202 IS NOT A 200 ──────────────────────────────────────────────────────────────────────
 *
 * The invite route answers 202 with `delivery` when the mail port suppressed or refused the message.
 * The invitation EXISTS in that case — the row is written, the token is minted, the seat is consumed
 * — and only the message did not go out. Reporting that as "ההזמנה נשלחה" is how an office waits a
 * week for somebody who was never told, and then invites them again and pays for a second seat. The
 * three outcomes get three different states here, from `deliveryText.ts`.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { CapacityRefusalNotice } from '../components/CapacityPanel';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import type { MailDeliveryReason } from '../api/contract';
import { MAIL_DELIVERY_REASONS } from '../api/contract';
import { deliveryTextFor } from '../api/deliveryText';
import { routes } from '../routes';

const TITLE_HE = 'הזמנת משתמש';

type Outcome =
  | {
      readonly kind: 'invited';
      readonly invitationUid: string;
      /** `null` ⇒ the provider accepted the message. Otherwise the office has to be told which. */
      readonly delivery: MailDeliveryReason | null;
    }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function isDeliveryReason(value: unknown): value is MailDeliveryReason {
  return typeof value === 'string' && (MAIL_DELIVERY_REASONS as readonly string[]).includes(value);
}

/**
 * THE FIELDS THIS FORM SENDS. Exported so a test can assert the list, because the assertion is
 * about what is ABSENT and an absence is not otherwise checkable.
 *
 * OA-0001 §3.7: no `roleCode`, no `capabilities`, no `companyUids`, no `password`, no `isAdmin`.
 */
export const INVITATION_FIELDS: readonly string[] = ['displayName', 'email'];

export function TenantUserInvitationScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      // A write. Never retried — the client cannot know whether the server committed, and a
      // duplicated invitation is a second seat consumed against a limit.
      const created = await callOperation('tenant.user.invite', {
        body: { displayName, email },
      });
      setOutcome({
        kind: 'invited',
        invitationUid: created.invitationUid,
        delivery: isDeliveryReason(created.delivery) ? created.delivery : null,
      });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'משתמשי המשרד', href: routes.tenantUsers }}
      provenanceHe="ההזמנה נשלחת בדואר אלקטרוני. המערכת אינה יוצרת סיסמה ואינה מציגה סיסמה בשום שלב."
    >
      <form className="form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">שם המשתמש</span>
          <input
            className="field__input"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">דואר אלקטרוני</span>
          <input
            className="field__input"
            type="email"
            dir="ltr"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
          />
        </label>

        {/*
          The explanation of the missing field. It is part of the form, not a footnote: an
          administrator looking for a role dropdown must find out here that there is not one, and why.
        */}
        <section className="notice" data-tone="info">
          <h2 className="notice__title">המוזמן מגיע ללא הרשאות</h2>
          <p className="notice__body">
            הזמנה אינה כוללת תפקיד. המשתמש שיצטרף לא יוכל לעשות דבר עד שיוענק לו תפקיד, ולא יראה אף
            חברה עד שתוענק לו גישה אליה. שתי הפעולות האלה נפרדות זו מזו ונפרדות מההזמנה — כך אי אפשר
            להקים משתמש חדש ולתת לו הרשאות באותה פעולה.
          </p>
          {/*
            The two acts, named and reachable. An administrator who cannot see where the granting
            happens will look for it on this form, which is precisely the field that is not here.
          */}
          <p className="notice__body">
            הענקת תפקיד נעשית במסך <a href={routes.tenantRoles}>תפקידים והרשאות</a>, והענקת גישה
            לחברות נעשית במסך <a href={routes.tenantCompanyAccess}>הרשאות לחברות</a>.
          </p>
        </section>

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'שולח…' : 'שליחת הזמנה'}
          </button>
          <a className="btn" href={routes.tenantUsers}>
            ביטול
          </a>
        </div>
      </form>

      {outcome === null ? null : outcome.kind === 'invited' ? (
        (() => {
          // Three outcomes, three states. The invitation exists in all three; what differs is whether
          // anybody was told, and what the office must now do about it.
          const delivery = deliveryTextFor(outcome.delivery);
          return (
            <StateBlock
              titleHe={delivery.titleHe}
              bodyHe={`${delivery.explanationHe} המשתמש יקבע סיסמה בעצמו, ועד להצטרפותו הוא מופיע ברשימה במצב ״הוזמן״.`}
              tone={delivery.tone === 'ok' ? 'empty' : delivery.tone === 'warn' ? 'unavailable' : 'error'}
              nextStep={
                <>
                  <p>{delivery.nextStepHe}</p>
                  <div className="actions-row">
                    <a className="btn btn--primary" href={routes.tenantRoles}>
                      הענקת תפקיד
                    </a>
                    <a className="btn" href={routes.tenantCompanyAccess}>
                      הענקת גישה לחברות
                    </a>
                  </div>
                </>
              }
            />
          );
        })()
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          renderConflict={(failure) => {
            /*
              08 §7 ON THIS SCREEN TOO. The seat limit is the refusal an invitation actually hits, and
              it now arrives with the count, the limit and the address that resolves it — the same
              component `TenantCompaniesScreen` uses for the company limit, so the two never drift.
            */
            if (
              failure.body.error !== 'limit_reached' ||
              failure.body.current === undefined ||
              failure.body.limit === undefined
            ) {
              return null;
            }
            return (
              <CapacityRefusalNotice
                refusal={{
                  reason: 'limit_reached',
                  kind: 'seat',
                  current: failure.body.current,
                  limit: failure.body.limit,
                  // An address inside this product. The server's `upgradePath` is not pasted into an
                  // `href` unexamined: `routes.ts` is the only place a URL is built (09 §1.5).
                  upgradeHref: routes.tenantSubscription,
                }}
              />
            );
          }}
        />
      )}
    </AppShell>
  );
}
